import { useEffect, useState } from "react";
import { projectById } from "../data/projects";

export const CaseStudy = ({ projectId, onClose, onOpen }) => {
  const project = projectById[projectId];
  const [index, setIndex] = useState(0);
  const [dir, setDir] = useState(1);

  const slides = project?.slides ?? [];
  const total = slides.length;
  const slide = slides[index];
  const nextProject = project ? projectById[project.next] : null;

  useEffect(() => {
    setIndex(0);
    setDir(1);
  }, [projectId]);

  useEffect(() => {
    if (!project) return undefined;
    const prevOverflow = document.body.style.overflow;
    document.documentElement.classList.add("case-open");
    document.body.style.overflow = "hidden";
    return () => {
      document.documentElement.classList.remove("case-open");
      document.body.style.overflow = prevOverflow;
    };
  }, [project]);

  useEffect(() => {
    if (!project) return undefined;
    const onKey = (event) => {
      if (event.key === "Escape") onClose();
      if (event.key === "ArrowRight") {
        setDir(1);
        setIndex((current) => (current + 1) % total);
      }
      if (event.key === "ArrowLeft") {
        setDir(-1);
        setIndex((current) => (current - 1 + total) % total);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [project, total, onClose]);

  const go = (step) => {
    if (!total) return;
    setDir(step);
    setIndex((current) => (current + step + total) % total);
  };

  if (!project || !slide) return null;

  const isHero = index === 0;

  return (
    <div className="case-layer" role="dialog" aria-modal="true" aria-labelledby="case-title">
      <div className="case-top">
        <button className="case-back" type="button" onClick={onClose}>
          <svg width="18" height="14" viewBox="0 0 18 14" fill="none" aria-hidden="true">
            <path
              d="M7 1 L1 7 L7 13 M1 7 H17"
              stroke="currentColor"
              strokeWidth="1.6"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
          <span>Back</span>
        </button>
        <div className="case-pager">
          <span>
            {String(index + 1).padStart(2, "0")} / {String(total).padStart(2, "0")}
          </span>
          <button type="button" aria-label="Previous slide" onClick={() => go(-1)}>
            ‹
          </button>
          <button type="button" aria-label="Next slide" onClick={() => go(1)}>
            ›
          </button>
        </div>
      </div>

      <div className="case-stage">
        <article className={`case-slide ${isHero ? "is-hero" : ""}`} key={`${project.id}-${index}`} data-dir={dir}>
          {isHero ? (
            <>
              <div className="case-hero-copy">
                <p className="case-kicker">{project.client}</p>
                <h1 id="case-title">{project.title}</h1>
                <p className="case-tagline">{project.tagline}</p>
                <p className="case-intro">{project.intro}</p>
                <dl className="case-meta">
                  <div>
                    <dt>Role</dt>
                    <dd>{project.role}</dd>
                  </div>
                  <div>
                    <dt>Company</dt>
                    <dd>{project.client}</dd>
                  </div>
                  <div>
                    <dt>Shipped</dt>
                    <dd>{project.shipped}</dd>
                  </div>
                  <div>
                    <dt>Platform</dt>
                    <dd>{project.platform}</dd>
                  </div>
                </dl>
                {(project.link || project.partnerLink) && (
                  <div className="case-links">
                    {project.link && (
                      <a className="case-store" href={project.link} target="_blank" rel="noopener noreferrer">
                        {project.linkLabel || "View live"}
                      </a>
                    )}
                    {project.partnerLink && (
                      <a className="case-store" href={project.partnerLink} target="_blank" rel="noopener noreferrer">
                        {project.partnerLabel || "Read the partnership"}
                      </a>
                    )}
                  </div>
                )}
              </div>
              <figure className={`case-hero-fig tone-${project.tone} fit-${project.fit}`}>
                <img src={slide.img || project.img} alt={slide.alt || project.alt} />
              </figure>
            </>
          ) : (
            <>
              <div className="case-rail">
                <p className="case-num">{String(index).padStart(2, "0")}</p>
                <p className="case-eye">{slide.kicker}</p>
              </div>
              <div className="case-body">
                <h2>
                  {slide.title}
                </h2>
                <p>{slide.body}</p>
                {slide.rows && (
                  <div className="case-rows">
                    {slide.rows.map((row) => (
                      <div className="case-row" key={row.key}>
                        <p>
                          <strong>{row.key}</strong>
                          <span>{row.sub}</span>
                        </p>
                        <p>{row.desc}</p>
                      </div>
                    ))}
                  </div>
                )}
                {slide.stats && (
                  <div className="case-stats">
                    {slide.stats.map((stat) => (
                      <p key={stat.label}>
                        <strong>{stat.value}</strong>
                        <span>{stat.label}</span>
                      </p>
                    ))}
                  </div>
                )}
                {slide.quote && <blockquote className="case-quote">“{slide.quote}”</blockquote>}
                {slide.link && (
                  <a className="case-store" href={slide.link} target="_blank" rel="noopener noreferrer">
                    {slide.linkLabel || "View live"}
                  </a>
                )}
                {slide.img && (
                  <figure className={`case-fig tone-${project.tone} fit-${project.fit}`}>
                    <img src={slide.img} alt={slide.alt || ""} />
                  </figure>
                )}
                {index === total - 1 && project.link && (
                  <a className="case-store" href={project.link} target="_blank" rel="noopener noreferrer">
                    {project.linkLabel || "View live"}
                  </a>
                )}
                {index === total - 1 && nextProject && (
                  <button className="case-next" type="button" onClick={() => onOpen(nextProject.id)}>
                    <span>Read next</span>
                    <strong>{nextProject.title}</strong>
                    <em>{nextProject.tagline}</em>
                  </button>
                )}
              </div>
            </>
          )}
        </article>
      </div>

      <div className="case-dots" role="tablist" aria-label="Case study slides">
        {slides.map((item, i) => (
          <button
            key={item.kicker}
            type="button"
            role="tab"
            aria-selected={i === index}
            aria-label={`${item.kicker}, slide ${i + 1}`}
            className={i === index ? "is-on" : ""}
            onClick={() => {
              setDir(i > index ? 1 : -1);
              setIndex(i);
            }}
          />
        ))}
      </div>
    </div>
  );
};
